import type { OptionLeg } from "@/types/options";

const D_LEG_RE = /^d_leg_(\d+)$/;
const T_LEG_RE = /^t_leg_(\d+)$/;
const S_LEG_RE = /^s_leg_(\d+)$/;
const Q_LEG_RE = /^q_leg_(\d+)$/;
const LEG_ORDER_RE = /^leg_order_(\d+)$/;
const CUSTOM_LEG_PARAM_RE =
  /^(d_leg|t_leg|s_leg|q_leg|premium_leg|leg_off|expiry_leg)_(\d+)$/;
export const ANCHOR_LOCKED_PARAM = "anchor_locked";

/**
 * Effective spot for leg generation / BS pricing / payoff sampling.
 *
 * `entrySpot` is the simulator's global anchor (where the strategy was
 * placed); `params.pivot` is the user-draggable bend on top. Strikes
 * derive from `entrySpot + pivot`, never from values stored in JSONB —
 * layer params hold only relative geometry, no ticker-coupled prices.
 */
export function effectiveSpot(
  entrySpot: number,
  params: Record<string, number>,
): number {
  return entrySpot + (params.pivot ?? 0);
}

export function isAnchorLocked(params: Record<string, number>): boolean {
  return params[ANCHOR_LOCKED_PARAM] === 1;
}

export function isMarketPinned(params: Record<string, number>): boolean {
  return !isAnchorLocked(params);
}

export function setAnchorLocked(
  params: Record<string, number>,
  locked: boolean,
): Record<string, number> {
  const next = { ...params };
  if (locked) {
    next[ANCHOR_LOCKED_PARAM] = 1;
  } else {
    delete next[ANCHOR_LOCKED_PARAM];
  }
  return next;
}

export function setMarketPinned(
  params: Record<string, number>,
  pinned: boolean,
): Record<string, number> {
  return setAnchorLocked(params, !pinned);
}

export function preserveAnchorAcrossEntrySpot(
  params: Record<string, number>,
  previousEntrySpot: number | null,
  nextEntrySpot: number | null,
): Record<string, number> {
  // Unpinned layers are frozen in price space. When the market anchor changes,
  // offset pivot by the inverse move so effectiveSpot remains stable.
  if (
    isMarketPinned(params) ||
    previousEntrySpot == null ||
    nextEntrySpot == null ||
    !Number.isFinite(previousEntrySpot) ||
    !Number.isFinite(nextEntrySpot) ||
    previousEntrySpot === nextEntrySpot
  ) {
    return params;
  }
  return {
    ...params,
    pivot: (params.pivot ?? 0) + previousEntrySpot - nextEntrySpot,
  };
}

/**
 * Apply per-leg strike deltas from params to computed legs.
 * `d_leg_0`, `d_leg_1`, ... shift the strike of the corresponding leg index.
 * Deltas are additive ($5 increments expected but not enforced here).
 */
export function applyLegDeltas(
  legs: OptionLeg[],
  params: Record<string, number>,
): OptionLeg[] {
  let hasDeltas = false;
  for (const key in params) {
    if (D_LEG_RE.test(key)) {
      hasDeltas = true;
      break;
    }
  }
  if (!hasDeltas) return legs;

  return legs.map((leg, i) => {
    const delta = params[`d_leg_${i}`];
    if (delta == null || delta === 0) return leg;
    return { ...leg, strike: leg.strike + delta };
  });
}

/**
 * Apply per-leg type and side overrides from params.
 * `t_leg_0: 1` changes leg 0 type to "put" (0 = call, 1 = put).
 * `s_leg_0: -1` changes leg 0 side to "short" (1 = long, -1 = short).
 * `q_leg_0: 2` changes leg 0 economic quantity to 2 contracts.
 */
export function applyLegOverrides(
  legs: OptionLeg[],
  params: Record<string, number>,
): OptionLeg[] {
  let hasOverrides = false;
  for (const key in params) {
    if (T_LEG_RE.test(key) || S_LEG_RE.test(key) || Q_LEG_RE.test(key)) {
      hasOverrides = true;
      break;
    }
  }
  if (!hasOverrides) return legs;

  return legs.map((leg, i) => {
    const typeVal = params[`t_leg_${i}`];
    const sideVal = params[`s_leg_${i}`];
    const quantityVal = params[`q_leg_${i}`];
    if (typeVal == null && sideVal == null && quantityVal == null) return leg;
    const quantity =
      quantityVal == null || !Number.isFinite(quantityVal)
        ? leg.quantity
        : Math.max(1, Math.round(quantityVal));
    return {
      ...leg,
      ...(typeVal != null
        ? { type: typeVal >= 1 ? ("put" as const) : ("call" as const) }
        : {}),
      ...(sideVal != null
        ? { side: sideVal < 0 ? ("short" as const) : ("long" as const) }
        : {}),
      quantity,
    };
  });
}

/**
 * Set a single leg delta, cleaning up zeroed-out keys.
 * Returns a new params object — does not mutate.
 * If the resulting delta is 0, the key is removed (clean deletion).
 */
export function setLegDelta(
  params: Record<string, number>,
  legIndex: number,
  value: number,
): Record<string, number> {
  const key = `d_leg_${legIndex}`;
  const next = { ...params };
  if (value === 0) {
    delete next[key];
  } else {
    next[key] = value;
  }
  return next;
}

// ── Per-leg enable/disable ──────────────────────────────────────────────────

const LEG_OFF_RE = /^leg_off_(\d+)$/;

/**
 * Filter out disabled legs from an array.
 * `leg_off_0: 1`, `leg_off_1: 1`, ... mark the corresponding leg index as disabled.
 * Index refers to position in the input array (before any filtering).
 */
export function filterDisabledLegs(
  legs: OptionLeg[],
  params: Record<string, number>,
): OptionLeg[] {
  let hasOffs = false;
  for (const key in params) {
    if (LEG_OFF_RE.test(key)) {
      hasOffs = true;
      break;
    }
  }
  if (!hasOffs) return legs;
  return legs.filter((_, i) => params[`leg_off_${i}`] !== 1);
}

/** Check whether a specific leg index is disabled. */
export function isLegOff(
  params: Record<string, number>,
  legIndex: number,
): boolean {
  return params[`leg_off_${legIndex}`] === 1;
}

export function activeLegIndexForOriginalIndex(
  params: Record<string, number>,
  originalIndex: number,
): number | null {
  if (isLegOff(params, originalIndex)) return null;

  let activeIndex = 0;
  for (let i = 0; i < originalIndex; i++) {
    if (!isLegOff(params, i)) activeIndex += 1;
  }
  return activeIndex;
}

export function originalLegIndexForActiveIndex(
  params: Record<string, number>,
  activeIndex: number,
  legCount?: number,
): number {
  const boundedLegCount =
    legCount != null && Number.isFinite(legCount)
      ? Math.max(0, Math.round(legCount))
      : null;
  const scanLimit =
    boundedLegCount ?? activeIndex + Object.keys(params).length + 8;
  let currentActiveIndex = 0;

  for (let originalIndex = 0; originalIndex < scanLimit; originalIndex++) {
    if (isLegOff(params, originalIndex)) continue;
    if (currentActiveIndex === activeIndex) return originalIndex;
    currentActiveIndex += 1;
  }

  return activeIndex;
}

/**
 * Return original leg indexes in their saved presentation order.
 *
 * Leg-specific pricing and override params remain keyed to the registry's
 * original indexes. Keeping presentation order separate prevents a visual
 * reorder from attaching a strike, premium, or expiry override to another leg.
 */
export function orderedLegIndexes(
  params: Record<string, number>,
  legCount: number,
): number[] {
  const count = Math.max(0, Math.round(legCount));
  const naturalOrder = Array.from({ length: count }, (_, index) => index);
  const savedOrder = naturalOrder.map((slot) => params[`leg_order_${slot}`]);

  if (
    savedOrder.some(
      (index) => !Number.isInteger(index) || index < 0 || index >= count,
    ) ||
    new Set(savedOrder).size !== count
  ) {
    return naturalOrder;
  }

  return savedOrder;
}

export function reorderLegDisplayOrder(
  params: Record<string, number>,
  legCount: number,
  activeOriginalIndex: number,
  overOriginalIndex: number,
): Record<string, number> {
  const order = orderedLegIndexes(params, legCount);
  const fromIndex = order.indexOf(activeOriginalIndex);
  const toIndex = order.indexOf(overOriginalIndex);
  if (fromIndex < 0 || toIndex < 0 || fromIndex === toIndex) return params;

  const nextOrder = [...order];
  const [moved] = nextOrder.splice(fromIndex, 1);
  nextOrder.splice(toIndex, 0, moved);

  const next = { ...params };
  Object.keys(next).forEach((key) => {
    if (LEG_ORDER_RE.test(key)) delete next[key];
  });
  if (nextOrder.every((originalIndex, slot) => originalIndex === slot)) {
    return next;
  }
  nextOrder.forEach((originalIndex, slot) => {
    next[`leg_order_${slot}`] = originalIndex;
  });
  return next;
}

export function compactCustomLegParams(
  params: Record<string, number>,
  selectedIndexes: Set<number>,
  legCount: number,
): Record<string, number> {
  if (selectedIndexes.size === 0 || selectedIndexes.size >= legCount) {
    return params;
  }

  const nextParams: Record<string, number> = {};
  const indexMap = new Map<number, number>();
  let nextIndex = 0;

  for (let index = 0; index < legCount; index++) {
    if (selectedIndexes.has(index)) continue;
    indexMap.set(index, nextIndex);
    nextIndex += 1;
  }

  for (const [key, value] of Object.entries(params)) {
    if (LEG_ORDER_RE.test(key)) continue;
    const match = CUSTOM_LEG_PARAM_RE.exec(key);
    if (!match) {
      if (key !== "leg_count") nextParams[key] = value;
      continue;
    }

    const mappedIndex = indexMap.get(Number(match[2]));
    if (mappedIndex == null) continue;
    nextParams[`${match[1]}_${mappedIndex}`] = value;
  }

  nextParams.leg_count = nextIndex;
  const nextOrder = orderedLegIndexes(params, legCount)
    .filter((originalIndex) => !selectedIndexes.has(originalIndex))
    .map((originalIndex) => indexMap.get(originalIndex))
    .filter((index): index is number => index != null);
  if (!nextOrder.every((originalIndex, slot) => originalIndex === slot)) {
    nextOrder.forEach((originalIndex, slot) => {
      nextParams[`leg_order_${slot}`] = originalIndex;
    });
  }
  return nextParams;
}

export function compactDisabledCustomLegParams(
  params: Record<string, number>,
): Record<string, number> {
  const legCount = Math.max(1, Math.round(params.leg_count ?? 1));
  const selectedIndexes = new Set<number>();
  for (let index = 0; index < legCount; index++) {
    if (isLegOff(params, index)) selectedIndexes.add(index);
  }
  return compactCustomLegParams(params, selectedIndexes, legCount);
}

/**
 * Set or clear a single leg's disabled state.
 * Returns a new params object — does not mutate.
 * When off === false, the key is removed (clean deletion).
 */
export function setLegOff(
  params: Record<string, number>,
  legIndex: number,
  off: boolean,
): Record<string, number> {
  const key = `leg_off_${legIndex}`;
  const next = { ...params };
  if (off) {
    next[key] = 1;
  } else {
    delete next[key];
  }
  return next;
}
